import { useContext, useState } from "react";
import { RyeButton } from "../rye/RyeButton";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { buildClasses } from "../utils/buildClasses";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import {
  ItemsGroup,
  RyeDraggableGroupedList,
} from "../rye/RyeDraggableGroupedList";
import {
  firestoreCreateCategoryGroup,
  firestoreUpdateCategoriesOrder,
  firestoreUpdateCategoryGroupsOrder,
} from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import dayjs from "dayjs";
import { CategoryItem } from "./CategoryItem";
import { CategoryGroupItem } from "./CategoryGroupItem";
import { createContextWithSetter } from "../utils/ContextUtils";
import { DeleteCategoryModal } from "./DeleteCategoryModal";
import { DeleteCategoryGroupModal } from "./DeleteCategoryGroupModal";
import { useRyeModal } from "../rye/RyeModal";
import { CategoryStarterSetup } from "./CategoryStarterSetup";
import { RyeSkeletonList } from "../rye/RyeSkeleton";
import { LoadingInitialDataContext } from "../LoadUser";

export const FocusCategoryIndexContext = createContextWithSetter<{
  groupId: string;
  categoryIndex: number;
} | null>(null);
export const FocusCategoryGroupIndexContext = createContextWithSetter<
  number | null
>(null);

export function CategoriesScreen(): JSX.Element {
  const [focusCategoryIndex, setFocusCategoryIndex] = useState<{
    groupId: string;
    categoryIndex: number;
  } | null>(null);
  const [focusCategoryGroupIndex, setFocusCategoryGroupIndex] = useState<
    number | null
  >(null);
  const user = useContext(UserContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const categories = useContext(CategoriesContext);
  const loading = useContext(LoadingInitialDataContext);
  // The starter setup is for users with no categories, so it waits too.
  const isLoading = loading.categories || loading.category_groups;
  const { openModal } = useRyeModal();

  const groupedCategoriesBuilder: Map<string, ItemsGroup> = new Map();
  Array.from(categoryGroups.values())
    .sort((a, b) => a.order_position - b.order_position)
    .forEach((categoryGroup) => {
      groupedCategoriesBuilder.set(categoryGroup.id, {
        id: categoryGroup.id,
        value: (
          <CategoryGroupItem
            categoryGroup={categoryGroup}
            openDeleteCategoryGroupModal={() =>
              openModal(
                <DeleteCategoryGroupModal categoryGroup={categoryGroup} />,
              )
            }
          />
        ),
        items: [],
      });
    });
  Array.from(categories.values())
    .sort((a, b) => a.order_position - b.order_position)
    .forEach((category) => {
      groupedCategoriesBuilder.get(category.category_group_id)!.items.push({
        id: category.id,
        value: (
          <CategoryItem
            category={category}
            openDeleteCategoryModal={() =>
              openModal(<DeleteCategoryModal category={category} />)
            }
          />
        ),
      });
    });
  const groupedCategories: ItemsGroup[] = Array.from(
    groupedCategoriesBuilder.values(),
  );

  return (
    <FocusCategoryGroupIndexContext.Provider
      value={[focusCategoryGroupIndex, setFocusCategoryGroupIndex]}
    >
      <FocusCategoryIndexContext.Provider
        value={[focusCategoryIndex, setFocusCategoryIndex]}
      >
        <RyeMainContentPanel
          title="Categories"
          roundedTl={false}
          roundedBl={false}
          actions={
            categories.size > 0 || categoryGroups.size > 0 ? (
              <RyeButton
                text="New Category Group"
                icon="add"
                size="sm"
                variant="outlined"
                vibe="subdued"
                shadow="sm"
                onClick={() => {
                  firestoreCreateCategoryGroup(user.uid, {
                    name: "New Group",
                    created_timestamp_secs: dayjs().unix(),
                    order_position: categoryGroups.size,
                  });
                  setFocusCategoryGroupIndex(categoryGroups.size);
                }}
              />
            ) : undefined
          }
        >
          {isLoading ? <RyeSkeletonList /> : <CategoryStarterSetup />}
          <div
            className={buildClasses(
              "min-w-[1300px]",
              !isLoading && (categories.size > 0 || categoryGroups.size > 0)
                ? ""
                : "hidden",
            )}
          >
            <RyeDraggableGroupedList
              inputGroupedItems={groupedCategories}
              onGroupOrderUpdate={(newIdList: string[]) =>
                firestoreUpdateCategoryGroupsOrder(user.uid, newIdList)
              }
              onItemOrderUpdate={(newGroupedItems: ItemsGroup[]) =>
                firestoreUpdateCategoriesOrder(user.uid, newGroupedItems)
              }
              collapsedItemSpacerHeight={3}
              collapsedGroupSpacerHeight={15}
              backgroundColor="transparent"
              groupClass="group/categorygroupitem"
            />
          </div>
        </RyeMainContentPanel>
      </FocusCategoryIndexContext.Provider>
    </FocusCategoryGroupIndexContext.Provider>
  );
}
